// src/components/CustomerFlow/Steps/PaymentProcessingStep.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { trackMobilePaymentCardSaved } from '../../../analytics/events';
import './PaymentProcessingStep.css';

const PaymentProcessingStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  // Extract IDs from flowData provided by MobileCustomerFlowPage
  const customerId = flowData?.tokenCustomerId;
  const purchaseRequestId = flowData?.tokenPurchaseRequestId;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPaymentWidgetOpen, setIsPaymentWidgetOpen] = useState(false);

  const paymentsInstanceRef = useRef(null);
  const isOpeningWidgetRef = useRef(false);
  const pollingRunRef = useRef(0);
  const completionTriggeredRef = useRef(false);

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;
  const tokenScope = (() => {
    try {
      return JSON.parse(localStorage.getItem('temp_tokenScope') || '{}');
    } catch (error) {
      return {};
    }
  })();
  const expectedHolderId = tokenScope.customer_dni
    || tokenScope.dni
    || tokenScope.document_number
    || tokenScope.customer?.dni
    || localStorage.getItem('temp_tokenDni')
    || flowData?.customer?.dni
    || null;
  const widgetDocument = expectedHolderId;

  // Auto-activate the step if it is pending
  useEffect(() => {
    const currentStepStatus = stepStatuses['PaymentStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'PaymentStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  const fetchPaymentStatus = useCallback(async ({ updateState = true } = {}) => {
    const response = await api.get(
      `/customers/${customerId}/purchase-requests/${purchaseRequestId}/payment-status`
    );

    if (updateState) {
      setPaymentStatus(response.data);
    }

    return response.data;
  }, [customerId, purchaseRequestId]);

  useEffect(() => {
    if (!customerId || !purchaseRequestId) {
      return;
    }

    let isActive = true;

    fetchPaymentStatus({ updateState: false })
      .then((status) => {
        if (!isActive) return;
        setPaymentStatus(status);
        setErrorMessage('');
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Error fetching payment status:', error);
        setErrorMessage('No pudimos cargar el estado de pago. Revisa tu conexión o vuelve a intentarlo.');
      });

    return () => {
      isActive = false;
    };
  }, [customerId, purchaseRequestId, fetchPaymentStatus, reloadTrigger]);

  const requiresDownpayment = !!paymentStatus?.downpayment?.required;
  const expectedDownpaymentAmount = Number(paymentStatus?.downpayment?.expected_amount || 0);
  const hasLoadedPaymentStatus = paymentStatus !== null;

  const completePaymentStepFromWidget = useCallback(async (cardData) => {
    const currentRun = pollingRunRef.current;

    if (completionTriggeredRef.current || currentRun !== pollingRunRef.current) {
      return false;
    }

    completionTriggeredRef.current = true;
    setIsCardSaved(true);
    setErrorMessage('');
    setIsPaymentWidgetOpen(false);
    paymentsInstanceRef.current?.closeWidget?.();

    const branchId = localStorage.getItem('temp_tokenBranchId');
    const cardBrand = cardData?.source?.brand || cardData?.source?.brand_name;

    trackMobilePaymentCardSaved({
      commerceId: branchId || 'mobile_flow',
      branchId: branchId || undefined,
      flowId: flowData?.flow_id,
      cardBrand
    });

    const stepData = {
      step_name: 'PaymentStep',
      step_status: 'processed',
      payment_setup_processed: true,
      widget_validated: true,
      requires_downpayment: requiresDownpayment,
      processed_timestamp: new Date().toISOString()
    };

    setTimeout(() => {
      if (currentRun === pollingRunRef.current && completionTriggeredRef.current) {
        onNext(stepData, 'processed');
      }
    }, 1200);

    return true;
  }, [flowData?.flow_id, onNext, requiresDownpayment]);

  const reloadWidget = useCallback(() => {
    pollingRunRef.current += 1;
    completionTriggeredRef.current = false;
    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(true);
    setIsPaymentWidgetOpen(false);
    setIsSubmitting(false);

    if (paymentsInstanceRef.current) {
      paymentsInstanceRef.current.destroy?.();
      paymentsInstanceRef.current.closeWidget?.();
      paymentsInstanceRef.current = null;
    }

    setReloadTrigger((prev) => prev + 1);
  }, []);

  // Load the Cirenio script and prepare the modal widget instance
  useEffect(() => {
    if (!hasLoadedPaymentStatus) {
      return;
    }

    // Verify required IDs before preparing the widget
    if (!customerId || !purchaseRequestId) {
      console.error('Missing required IDs:', { customerId, purchaseRequestId });
      setErrorMessage('Error: Faltan datos del cliente o solicitud de compra.');
      return;
    }

    if (!widgetDocument) {
      setIsWidgetLoading(false);
      setErrorMessage('Error: Faltan datos del cliente para iniciar el widget de pago.');
      return;
    }

    let isActive = true;
    let script = null;

    const initializeWidget = () => {
      if (!isActive || !window.CirenioPayments) {
        return;
      }

      if (paymentsInstanceRef.current) {
        setIsWidgetLoading(false);
        return;
      }

      const paymentsInstance = new window.CirenioPayments(
        publicKey,
        widgetDocument,
        {
          showCloseButton: true,
          externalId: `purchase_request:${purchaseRequestId}`,
          flowType: requiresDownpayment ? 'payment' : 'tokenization',
          subjectType: 'purchase_request',
          subjectId: String(purchaseRequestId),
          paymentContext: requiresDownpayment ? 'downpayment_debit_card' : 'tokenization_only',
          advancePayment: requiresDownpayment ? {
            amount: expectedDownpaymentAmount,
          } : undefined,
          paymentConcept: requiresDownpayment ? 'Anticipo compra retail' : undefined,
          paymentDescription: requiresDownpayment ? 'Anticipo de compra financiada con debito' : undefined,
          firstName: flowData?.customer?.firstName,
          lastName: flowData?.customer?.lastName,
          email: flowData?.customer?.email,
        }
      );

      paymentsInstance.on('widget_start', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setIsPaymentWidgetOpen(true);
        }
      });

      paymentsInstance.on('widget_closed', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setIsPaymentWidgetOpen(false);
        }
      });

      paymentsInstance.on('tokenization_success', async (cardData) => {
        if (!isActive) return;

        const cardHolderId = cardData?.source?.holder?.identification;

        if (cardHolderId && expectedHolderId && cardHolderId !== expectedHolderId) {
          setErrorMessage('La tarjeta debe estar a nombre del cliente.');
          reloadWidget();
          return;
        }

        setIsSubmitting(true);
        await completePaymentStepFromWidget(cardData);
        if (isActive) {
          setIsSubmitting(false);
        }
      });

      paymentsInstance.on('tokenization_error', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setErrorMessage('Hubo un problema al procesar tu tarjeta. Por favor, intenta nuevamente.');
        }
      });

      paymentsInstanceRef.current = paymentsInstance;
      setIsWidgetLoading(false);
    };

    if (window.CirenioPayments) {
      initializeWidget();
    } else {
      setIsWidgetLoading(true);
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initializeWidget;
      script.onerror = () => {
        setIsWidgetLoading(false);
        setErrorMessage('No se pudo cargar el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
      };
      document.body.appendChild(script);
    }

    return () => {
      isActive = false;
      pollingRunRef.current += 1;
      isOpeningWidgetRef.current = false;
      if (paymentsInstanceRef.current) {
        paymentsInstanceRef.current.destroy?.();
        paymentsInstanceRef.current.closeWidget?.();
        paymentsInstanceRef.current = null;
      }
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [
    scriptSrc,
    publicKey,
    customerId,
    purchaseRequestId,
    reloadTrigger,
    widgetDocument,
    expectedHolderId,
    flowData?.customer?.firstName,
    flowData?.customer?.lastName,
    flowData?.customer?.email,
    requiresDownpayment,
    expectedDownpaymentAmount,
    hasLoadedPaymentStatus,
    reloadWidget,
    completePaymentStepFromWidget,
  ]);

  const openPaymentWidget = async () => {
    if (isOpeningWidgetRef.current || isPaymentWidgetOpen || isSubmitting || isCardSaved) {
      return;
    }

    if (!paymentsInstanceRef.current) {
      setErrorMessage('El widget de pagos todavia se esta preparando. Intenta nuevamente en unos segundos.');
      return;
    }

    isOpeningWidgetRef.current = true;
    setErrorMessage('');
    setIsWidgetLoading(true);
    setIsPaymentWidgetOpen(true);

    try {
      const opened = await paymentsInstanceRef.current.openWidget();

      if (opened === false) {
        setIsPaymentWidgetOpen(false);
        setErrorMessage('No se pudo abrir el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
      }
    } catch (error) {
      console.error('Error opening payment widget:', error);
      setIsPaymentWidgetOpen(false);
      setErrorMessage('No se pudo abrir el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
    } finally {
      isOpeningWidgetRef.current = false;
      setIsWidgetLoading(false);
    }
  };

  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    if (
      errorMessage.includes('No se pudo cargar el widget de pagos')
      || errorMessage.includes('No pudimos cargar el estado de pago')
    ) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          {errorMessage}{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            Reintentar
          </button>.
        </div>
      );
    }

    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <FontAwesomeIcon icon={faCreditCard} className="me-2" />
        {errorMessage}
      </div>
    );
  };

  const InfoModal = () => {
    if (!showInfoModal) return null;

    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="modal-content bg-white p-4 rounded-3" style={{
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              {requiresDownpayment ? 'Requisitos para pagar el anticipo' : 'Requisitos de la Tarjeta'}
            </h6>
            <ul className="list-unstyled">
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe ser una tarjeta de débito
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar a tu nombre
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar vinculada a la cuenta donde recibís tus ingresos
              </li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              ¿Para qué se utilizará?
            </h6>
            <p className="text-muted mb-2">
              {requiresDownpayment
                ? 'Se utilizará para cobrar el anticipo de tu compra financiada. Cuando el pago quede confirmado, podremos continuar con el proceso.'
                : 'Se utilizará como medio alternativo de cobranza y quedará vinculada para futuras compras'}
            </p>
          </div>
          
          <div>
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Seguridad
            </h6>
            <p className="text-muted mb-0">
              Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales
            </p>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowInfoModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="payment-processing-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
          <h3>{requiresDownpayment ? 'Pago de Anticipo' : 'Método de Pago'}</h3>
      </div>

      <div className="step-body">
        <div className="text-center mb-4">
          <p className="lead mb-1">
            {requiresDownpayment ? 'Realizá el pago del anticipo con tu tarjeta de débito' : 'Configura tu método de pago para los débitos automáticos'}
          </p>
          <p className='lead'>
            {requiresDownpayment
              ? 'Usá una tarjeta de débito a tu nombre. Cuando el pago quede registrado, avanzaremos automaticamente.'
              : 'Usá una tarjeta de débito a tu nombre, vinculada a la cuenta donde recibís tus ingresos.'}
          </p>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={() => setShowInfoModal(true)}
            >
              <i className="bi bi-info-circle me-1"></i>
              {requiresDownpayment ? '¿Por qué te pedimos este pago?' : '¿Para qué necesitamos tu tarjeta?'}
            </button>
          </div>
        </div>

        {!isCardSaved && (
          <div className="payment-widget-launch-card text-center">
            <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-primary mb-3" />
            <h5 className="mb-2">
              {requiresDownpayment ? 'Pagar anticipo con tarjeta' : 'Cargar datos de la tarjeta'}
            </h5>
            <p className="text-muted mb-4">
              {requiresDownpayment
                ? 'El formulario seguro se abrira en pantalla completa para completar el pago.'
                : 'El formulario seguro se abrira en pantalla completa para vincular tu tarjeta.'}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={openPaymentWidget}
              disabled={isWidgetLoading || isPaymentWidgetOpen || isSubmitting || paymentStatus === null}
            >
              {(isWidgetLoading || isPaymentWidgetOpen || isSubmitting) && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {isSubmitting
                ? 'Validando...'
                : isPaymentWidgetOpen
                  ? 'Formulario abierto'
                  : requiresDownpayment
                    ? 'Abrir pago seguro'
                    : 'Abrir formulario seguro'}
            </button>
            {isPaymentWidgetOpen && (
              <p className="text-muted small mt-3 mb-0">
                Si cerras el formulario, podes volver a abrirlo desde este boton.
              </p>
            )}
          </div>
        )}

        {renderErrorAlert()}
      </div>

      {/* Status message */}
      {isCardSaved && (
        <div className="text-center mt-4">
          <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-success mb-3" />
          <h3 className="text-success">{requiresDownpayment ? '¡Pago registrado!' : '¡Tarjeta registrada!'}</h3>
          <p className="lead">
            {requiresDownpayment
              ? 'Recibimos la validación del pago. Estamos avanzando con el proceso...'
              : 'Recibimos los datos de tu tarjeta. Estamos avanzando con el proceso...'}
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      )}

      <InfoModal />
    </div>
  );
};

export default PaymentProcessingStep;
